import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { routingKeyToCounty } from '../../data/routingKeyToCounty';
import { ArrowRight, Search, Scale, Building2, Train, Users } from 'lucide-react';

const toTitleCase = (value: string) =>
  value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const PRIORITY_ORDER = ['dublin', 'cork', 'galway', 'limerick', 'waterford', 'kildare', 'wicklow', 'meath'];

const countySlugs = Array.from(
  new Set(Object.values(routingKeyToCounty).map((county) => county.toLowerCase()))
).sort((a, b) => {
  const aRank = PRIORITY_ORDER.indexOf(a);
  const bRank = PRIORITY_ORDER.indexOf(b);
  if (aRank === -1 && bRank === -1) return a.localeCompare(b);
  if (aRank === -1) return 1;
  if (bRank === -1) return -1;
  return aRank - bRank;
});

const comparisonPairs = countySlugs.flatMap((cityA, index) =>
  countySlugs.slice(index + 1).map((cityB) => ({
    slug: `${cityA}-vs-${cityB}`,
    cityA,
    cityB,
  }))
);

const CityComparisonsDiscoverPage: React.FC = () => {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return comparisonPairs;
    return comparisonPairs.filter(
      (pair) => pair.cityA.includes(normalized) || pair.cityB.includes(normalized)
    );
  }, [query]);

  return (
    <>
      <Helmet>
        <title>Ireland City Comparisons | Dublin vs Cork, Galway vs Limerick</title>
        <meta
          name="description"
          content="Compare Irish cities side by side for cost of living, rent, property prices, commute time and family fit. Explore relocation and investment-focused city comparison pages."
        />
        <meta
          name="keywords"
          content="dublin vs cork, galway vs limerick, ireland city comparison, moving to dublin or cork, cost of living city vs city ireland"
        />
        <link rel="canonical" href="https://rentsmart.ie/city-comparisons" />
      </Helmet>

      <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
        <section className="bg-slate-900 text-white pt-16 pb-20 px-4 relative overflow-hidden">
          <div className="absolute -top-24 -right-20 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />

          <div className="relative max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-6">
              <Scale className="h-4 w-4" /> City vs City 
            </div>
            <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
              Irish City Comparisons
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed max-w-3xl mx-auto mb-8">
              Compare Dublin vs Cork, Galway vs Limerick, and more. See side-by-side cost, rent,
              commute and lifestyle verdicts to decide where to live, work, or invest.
            </p>

            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search city comparison (e.g. dublin, cork, galway)"
                className="w-full rounded-xl bg-white text-slate-900 pl-12 pr-4 py-4 shadow-xl outline-none focus:ring-4 focus:ring-emerald-200"
              />
            </div>
          </div>
        </section>

        <main className="max-w-6xl mx-auto px-4 py-10">
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <Building2 className="h-5 w-5 text-emerald-600 mb-2" />
              <h2 className="text-sm font-black text-slate-900 mb-1">Property Price Gap</h2>
              <p className="text-sm text-slate-600">Median house prices compared city to city.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <Train className="h-5 w-5 text-sky-600 mb-2" />
              <h2 className="text-sm font-black text-slate-900 mb-1">Commute & Daily Friction</h2>
              <p className="text-sm text-slate-600">Understand time-cost trade-offs in daily movement.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <Users className="h-5 w-5 text-violet-600 mb-2" />
              <h2 className="text-sm font-black text-slate-900 mb-1">Family & Lifestyle Fit</h2>
              <p className="text-sm text-slate-600">Who each city suits best based on practical signals.</p>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
              No comparison pages match your search.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((pair) => (
                <Link
                  key={pair.slug}
                  to={`/city-comparisons/${pair.slug}`}
                  className="no-underline group rounded-3xl border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-xl transition-all"
                >
                  <p className="text-[10px] uppercase tracking-widest font-black text-slate-400 mb-2">
                    City Comparison
                  </p>
                  <h2 className="text-2xl font-black text-slate-900 mb-4">
                    {toTitleCase(pair.cityA)} <span className="text-slate-400">vs</span>{' '}
                    {toTitleCase(pair.cityB)}
                  </h2>
                  <div className="inline-flex items-center gap-2 text-sm font-black text-emerald-700">
                    Open Comparison <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default CityComparisonsDiscoverPage;
